// hooks/useStripeCards.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CREATE_SETUP_INTENT, GET_SAVED_CARDS, SET_DEFAULT_CARD, STRIPE_PUBLISHABLE_KEY, USER_BASE_URL } from "../../API";
import { loadStripe } from '@stripe/stripe-js';

export const useStripeCards = (profile, headers) => {
    const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
    const [savedCards, setSavedCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [loading, setLoading] = useState(true);

    const getSavedCards = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(USER_BASE_URL + GET_SAVED_CARDS, { headers });
            const cards = res.data.data.cards || [];
            const defaultPaymentMethodId = res.data.data.defaultPaymentMethodId || '';
            setSavedCards(cards);
            setSelectedCard(defaultPaymentMethodId);
            if (cards.length === 0) { await createSetupIntent() }
            else { setShowForm(false) }
        }
        catch (err) {
            toast.error("Failed to fetch saved cards");
            await createSetupIntent();
        }
        finally { setLoading(false); }
    }, [headers]);

    const createSetupIntent = async () => {
        try {
            const response = await axios.post(USER_BASE_URL + CREATE_SETUP_INTENT, {}, { headers });
            setClientSecret(response.data.data.clientSecret);
            setShowForm(true);
        } catch (err) { toast.error("Something went wrong") }
    };

    const handleSelectCard = async (cardId) => {
        if (selectedCard === cardId) return;
        try {
            setSelectedCard(cardId);
            await axios.post(USER_BASE_URL + SET_DEFAULT_CARD, { cardId }, { headers });
        } catch (err) { toast.error("Failed to update default card") }
    };

    const addNewCard = () => { createSetupIntent(); setShowForm(true) };

    const initStripe = async () => {
        try {
            setLoading(true);
            if (profile?.stripe_customer_id) { await getSavedCards() }
            else { await createSetupIntent() }
        }
        catch (err) { console.error(err) }
        finally { setLoading(false) }
    }

    useEffect(() => { initStripe() }, [profile?.stripe_customer_id, getSavedCards]);

    return {
        savedCards,
        selectedCard,
        clientSecret,
        showForm,
        loading,
        handleSelectCard,
        addNewCard,
        setShowForm,
        getSavedCards,
        createSetupIntent,
        stripePromise
    };
};